import {
  useEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { AlertTriangle } from '@huspy/briks-icons';
import Loader from '@shared/loader';
import briksTheme from '@shared/briks-theme';

const EMBED_LOAD_TIMEOUT_MS = 30000;

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  zIndex: 20,
} as const;

const EmbedErrorState = ({ onRetry }: { onRetry?: () => void }) => {
  const { t } = useTranslation();

  return (
    <Flex
      style={ overlayStyle }
      align='center'
      justify='center'
      bg='bg-secondary'
      px='6'
    >
      <Flex direction='column' align='center' gap='3' maxW={ 320 } textAlign='center'>
        <Flex
          h='12'
          w='12'
          align='center'
          justify='center'
          borderRadius='full'
          style={ { backgroundColor: briksTheme.colors['error.100'] } }
        >
          <AlertTriangle
            width={ 24 }
            height={ 24 }
            style={ { color: briksTheme.colors['error.800'] } }
            aria-hidden
          />
        </Flex>
        <Text size='sm' fontWeight='medium' role='alert'>
          {t('common.embed.error')}
        </Text>
        {onRetry && (
          <Button type='button' variant='outline' size='sm' onClick={ onRetry }>
            {t('common.embed.retry')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

type CredentiallessEmbedFrameProps = {
  url: string;
  title: string;
  onRetry: () => void;
};

const CredentiallessEmbedFrame = ({ url, title, onRetry }: CredentiallessEmbedFrameProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const { t } = useTranslation();

  // React types/DOM props don't support `credentialless` yet, and setting `src` in JSX
  // would start navigation before the attribute is applied — set both imperatively.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    iframe.setAttribute('credentialless', '');
    const handleLoad = () => setIsLoaded(true);
    iframe.addEventListener('load', handleLoad);
    iframe.src = url;

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [url]);

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHasTimedOut(true);
    }, EMBED_LOAD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoaded]);

  if (hasTimedOut) {
    return <EmbedErrorState onRetry={ onRetry } />;
  }

  return (
    <>
      {!isLoaded && (
        <Flex
          style={ overlayStyle }
          align='center'
          justify='center'
          bg='bg-secondary'
          role='status'
          aria-live='polite'
          aria-label={ t('common.embed.loading') }
        >
          <Loader size={ 32 } />
        </Flex>
      )}
      <iframe
        ref={ iframeRef }
        title={ title }
        style={ {
          border: 0, minHeight: '75vh', width: '100%', height: '100%',
        } }
      />
    </>
  );
};

type CredentiallessEmbedProps = {
  url: string;
  title: string;
};

const CredentiallessEmbed = ({ url, title }: CredentiallessEmbedProps) => {
  const [retryKey, setRetryKey] = useState(0);
  const hasUrl = url.trim().length > 0;

  return (
    <Box position='relative' minH='75vh' w='100%' overflow='hidden'>
      {hasUrl ? (
        <CredentiallessEmbedFrame
          key={ `${url}-${retryKey}` }
          url={ url }
          title={ title }
          onRetry={ () => setRetryKey((key) => key + 1) }
        />
      ) : (
        <EmbedErrorState />
      )}
    </Box>
  );
};

export default CredentiallessEmbed;
